import template from './sellops-order-modification-tracking-list.html.twig';
import './sellops-order-modification-tracking-list.scss';

const { Component, Mixin } = Shopware;
const { Criteria } = Shopware.Data;

Component.register('sellops-order-modification-tracking-list', {
    template,

    inject: ['repositoryFactory'],

    mixins: [
        Mixin.getByName('listing'),
        Mixin.getByName('notification'),
    ],

    data() {
        return {
            isLoading: false,
            items: null,
            sortBy: 'createdAt',
            sortDirection: 'DESC',
            total: 0,
            filterPaymentStatus: null,
            filterChangeType: null,
            filterModificationType: null,
            pendingCount: 0,
            refundCount: 0,
            completedCount: 0,
        };
    },

    metaInfo() {
        return {
            title: this.$createTitle(),
        };
    },

    computed: {
        repository() {
            return this.repositoryFactory.create('sellops_order_modification_tracking');
        },

        columns() {
            return [
                {
                    property: 'parentOrderNumber',
                    label: this.$tc('sellops-order-modification-tracking.list.columnParentOrderNumber'),
                    allowResize: true,
                    width: '130px',
                    sortable: false,
                },
                {
                    property: 'childOrderNumber',
                    label: this.$tc('sellops-order-modification-tracking.list.columnChildOrderNumber'),
                    allowResize: true,
                    width: '130px',
                    sortable: false,
                },
                {
                    property: 'modificationType',
                    label: this.$tc('sellops-order-modification-tracking.list.columnModificationType'),
                    allowResize: true,
                    width: '120px',
                },
                {
                    property: 'changeType',
                    label: this.$tc('sellops-order-modification-tracking.list.columnChangeType'),
                    allowResize: true,
                    width: '110px',
                },
                {
                    property: 'differenceAmount',
                    label: this.$tc('sellops-order-modification-tracking.list.columnDifference'),
                    allowResize: true,
                    align: 'right',
                    width: '120px',
                },
                {
                    property: 'paymentStatus',
                    label: this.$tc('sellops-order-modification-tracking.list.columnPaymentStatus'),
                    allowResize: true,
                    width: '180px',
                },
                {
                    property: 'paymentMethodName',
                    label: this.$tc('sellops-order-modification-tracking.list.columnPaymentMethod'),
                    allowResize: true,
                    width: '140px',
                },
                {
                    property: 'orderStatus',
                    label: this.$tc('sellops-order-modification-tracking.list.columnOrderStatus'),
                    allowResize: true,
                    width: '110px',
                },
                {
                    property: 'parentOrderTotal',
                    label: this.$tc('sellops-order-modification-tracking.list.columnParentTotal'),
                    allowResize: true,
                    align: 'right',
                    width: '120px',
                },
                {
                    property: 'childOrderTotal',
                    label: this.$tc('sellops-order-modification-tracking.list.columnChildTotal'),
                    allowResize: true,
                    align: 'right',
                    width: '120px',
                },
                {
                    property: 'createdAt',
                    label: this.$tc('sellops-order-modification-tracking.list.columnCreatedAt'),
                    allowResize: true,
                    width: '150px',
                },
            ];
        },

        paymentStatusOptions() {
            return [
                { value: '', label: this.$tc('sellops-order-modification-tracking.list.filterAll') },
                { value: 'pending_payment', label: this.$tc('sellops-order-modification-tracking.list.paymentStatusPending') },
                { value: 'refund_required', label: this.$tc('sellops-order-modification-tracking.list.paymentStatusRefund') },
                { value: 'completed', label: this.$tc('sellops-order-modification-tracking.list.paymentStatusCompleted') },
            ];
        },

        paymentStatusSelectOptions() {
            return [
                { value: 'pending_payment', label: this.$tc('sellops-order-modification-tracking.list.paymentStatusPending') },
                { value: 'refund_required', label: this.$tc('sellops-order-modification-tracking.list.paymentStatusRefund') },
                { value: 'completed', label: this.$tc('sellops-order-modification-tracking.list.paymentStatusCompleted') },
            ];
        },

        changeTypeOptions() {
            return [
                { value: '', label: this.$tc('sellops-order-modification-tracking.list.filterAll') },
                { value: 'increase', label: this.$tc('sellops-order-modification-tracking.list.changeTypeIncrease') },
                { value: 'decrease', label: this.$tc('sellops-order-modification-tracking.list.changeTypeDecrease') },
                { value: 'neutral', label: this.$tc('sellops-order-modification-tracking.list.changeTypeNeutral') },
            ];
        },

        modificationTypeOptions() {
            return [
                { value: '', label: this.$tc('sellops-order-modification-tracking.list.filterAll') },
                { value: 'supplement', label: this.$tc('sellops-order-modification-tracking.list.modificationTypeSupplement') },
                { value: 'replacement', label: this.$tc('sellops-order-modification-tracking.list.modificationTypeReplacement') },
            ];
        },

        listCriteria() {
            const criteria = new Criteria();

            criteria.setPage(this.page);
            criteria.setLimit(this.limit);
            criteria.setTotalCountMode(1);

            criteria.addSorting(Criteria.sort(this.sortBy, this.sortDirection));

            criteria.addAssociation('parentOrder');
            criteria.addAssociation('childOrder');

            if (this.filterPaymentStatus) {
                criteria.addFilter(Criteria.equals('paymentStatus', this.filterPaymentStatus));
            }

            if (this.filterChangeType) {
                criteria.addFilter(Criteria.equals('changeType', this.filterChangeType));
            }

            if (this.filterModificationType) {
                criteria.addFilter(Criteria.equals('modificationType', this.filterModificationType));
            }

            if (this.term) {
                criteria.setTerm(this.term);
            }

            return criteria;
        },
    },

    created() {
        this.getList();
    },

    methods: {
        async getList() {
            this.isLoading = true;

            try {
                const result = await this.repository.search(this.listCriteria, Shopware.Context.api);

                // Enrich items with flattened order numbers for easier grid rendering
                result.forEach((item) => {
                    item.parentOrderNumber = item.parentOrder ? item.parentOrder.orderNumber : (item.parentOrderId || '–');
                    item.childOrderNumber = item.childOrder ? item.childOrder.orderNumber : (item.childOrderId || '–');
                });

                this.items = result;
                this.total = result.total;

                await this.loadSummaryCounts();
            } catch (error) {
                this.createNotificationError({
                    message: error.message || 'Failed to load tracking data',
                });
            } finally {
                this.isLoading = false;
            }
        },

        async loadSummaryCounts() {
            try {
                const pendingCriteria = new Criteria();
                pendingCriteria.addFilter(Criteria.equals('paymentStatus', 'pending_payment'));
                pendingCriteria.setLimit(1);
                pendingCriteria.setTotalCountMode(1);
                const pendingResult = await this.repository.search(pendingCriteria, Shopware.Context.api);
                this.pendingCount = pendingResult.total;

                const refundCriteria = new Criteria();
                refundCriteria.addFilter(Criteria.equals('paymentStatus', 'refund_required'));
                refundCriteria.setLimit(1);
                refundCriteria.setTotalCountMode(1);
                const refundResult = await this.repository.search(refundCriteria, Shopware.Context.api);
                this.refundCount = refundResult.total;

                const completedCriteria = new Criteria();
                completedCriteria.addFilter(Criteria.equals('paymentStatus', 'completed'));
                completedCriteria.setLimit(1);
                completedCriteria.setTotalCountMode(1);
                const completedResult = await this.repository.search(completedCriteria, Shopware.Context.api);
                this.completedCount = completedResult.total;
            } catch (error) {
                // Summary counts are non-critical
            }
        },

        onSortColumn(column) {
            if (column.dataIndex) {
                this.sortBy = column.dataIndex;
            } else {
                this.sortBy = column.property;
            }

            this.sortDirection = this.sortDirection === 'ASC' ? 'DESC' : 'ASC';
            this.getList();
        },

        onChangeLanguage() {
            this.getList();
        },

        onFilterChange() {
            this.page = 1;
            this.getList();
        },

        capitalize(str) {
            if (!str) return '';
            return str.charAt(0).toUpperCase() + str.slice(1);
        },

        getVariantForChangeType(changeType) {
            const variants = {
                increase: 'warning',
                decrease: 'danger',
                neutral: 'info',
            };

            return variants[changeType] || 'neutral';
        },

        formatCurrency(amount, currencyIso) {
            return new Intl.NumberFormat(navigator.language, {
                style: 'currency',
                currency: currencyIso || 'EUR',
            }).format(amount || 0);
        },

        async onPaymentStatusChange(item, newStatus) {
            item.paymentStatus = newStatus;

            try {
                await this.repository.save(item, Shopware.Context.api);
                this.createNotificationSuccess({
                    message: this.$tc('sellops-order-modification-tracking.list.paymentStatusUpdated'),
                });
                await this.loadSummaryCounts();
            } catch (error) {
                this.createNotificationError({
                    message: error.message,
                });
                this.getList();
            }
        },

        onOpenParentOrder(item) {
            if (item.parentOrderId) {
                this.$router.push({ name: 'sw.order.detail', params: { id: item.parentOrderId } });
            }
        },

        onOpenChildOrder(item) {
            if (item.childOrderId) {
                this.$router.push({ name: 'sw.order.detail', params: { id: item.childOrderId } });
            }
        },

        goToSettings() {
            this.$router.push({ name: 'sellops.order.modification.tracking.config' });
        },
    },
});
